// Where the step card sits. On its own the card hangs off the target; while
// a popover is open — the target opened it, and doing the step happens in it
// — the card moves beside the popover, so the instruction stays readable
// exactly when the user needs it. The covered probe ignores such a popover
// (see `measureTarget`); only the card's position reacts to it.

import { TOUR_OVERLAY_ATTR } from './targetPlacement';

/** The card's distance from what it hangs off (Radix `sideOffset`). */
export const CARD_GAP = 16;
/** The card's distance from the viewport's edges (Radix `collisionPadding`). */
export const CARD_PAD = 12;
/** The card's width (`w-[300px]`). */
export const CARD_WIDTH = 300;
/** A typical card's height, for the frame before it mounts. */
export const CARD_HEIGHT = 180;
/** A popover this close to the target is the one it opened (Radix hangs one
 *  off its trigger at a small `sideOffset`); one farther away is someone
 *  else's, and the card ignores it. */
const OPENED_BY_TARGET_PX = 24;
/** Narrower than this the card isn't readable; it shrinks down to it to fit
 *  beside a popover (the card's `max-width` follows Radix's available width). */
export const CARD_MIN_WIDTH = 220;

export type CardSide = 'right' | 'left' | 'bottom' | 'top';

export interface CardSpot {
    side: CardSide;
    /** The box the card hangs off: the target, or the target with its open popovers. */
    anchor: DOMRect;
}

interface Size {
    width: number;
    height: number;
}

/** Beside a popover, left first: it opens below the trigger, so the reader's
 *  eye goes from the card across to the switches. */
const SIDES: readonly CardSide[] = ['left', 'right', 'bottom', 'top'];

function union(rects: DOMRect[]): DOMRect {
    const left = Math.min(...rects.map(rect => rect.left));
    const top = Math.min(...rects.map(rect => rect.top));
    const right = Math.max(...rects.map(rect => rect.right));
    const bottom = Math.max(...rects.map(rect => rect.bottom));
    return new DOMRect(left, top, right - left, bottom - top);
}

/** The gap between two boxes, 0 when they touch or overlap. */
function gapBetween(a: DOMRect, b: DOMRect): number {
    return Math.hypot(Math.max(0, b.left - a.right, a.left - b.right), Math.max(0, b.top - a.bottom, a.top - b.bottom));
}

/** Free space for the card on each side of `box`, along that side's axis. */
function roomAround(box: DOMRect, viewport: Size): Record<CardSide, number> {
    return {
        left: box.left - CARD_GAP - CARD_PAD,
        right: viewport.width - CARD_PAD - box.right - CARD_GAP,
        top: box.top - CARD_GAP - CARD_PAD,
        bottom: viewport.height - CARD_PAD - box.bottom - CARD_GAP,
    };
}

/** Where the card goes: off the target, or — with popovers the target opened — on the
 *  first side of them it fits on at full width, then at its narrowest, and
 *  when nothing fits (a cramped window) on the roomiest side, so it is at
 *  most partly cut off. */
export function cardSpot(target: DOMRect, popovers: DOMRect[], viewport: Size, card: Size): CardSpot {
    const opened = popovers.filter(popover => gapBetween(target, popover) <= OPENED_BY_TARGET_PX);
    if (opened.length === 0) return { side: 'right', anchor: target };
    const anchor = union([target, ...opened]);
    const room = roomAround(anchor, viewport);
    const need = (side: CardSide, width: number) => (side === 'left' || side === 'right' ? width : card.height);
    const side =
        SIDES.find(side => room[side] >= need(side, card.width)) ??
        SIDES.find(side => room[side] >= need(side, CARD_MIN_WIDTH)) ??
        SIDES.reduce((best, side) => (room[side] / need(side, CARD_MIN_WIDTH) > room[best] / need(best, CARD_MIN_WIDTH) ? side : best));
    return { side, anchor };
}

/** The boxes of the Radix popovers, menus and listboxes open right now — not
 *  the tutorial's own card, not one animating closed, not a tooltip (its
 *  state is `delayed-open` / `instant-open`, never `open`). */
export function openPopoverRects(): DOMRect[] {
    return [...document.querySelectorAll<HTMLElement>('[data-radix-popper-content-wrapper] > [data-state="open"]')]
        .filter(content => !content.hasAttribute(TOUR_OVERLAY_ATTR))
        .map(content => content.getBoundingClientRect())
        .filter(rect => rect.width > 0 && rect.height > 0);
}
